
import { RevButton } from '@ds/components/Button'
import { RevContainer } from '@ds/components/Container'
import { RevIllustration } from '@ds/components/Illustration'
import { RevLink } from '@ds/components/Link'
import isEmpty from 'lodash/isEmpty'
import omit from 'lodash/omit'
import { stringify } from 'query-string'
import { mapGetters } from 'vuex'

import { COUNTRIES } from '@config/constants'
import { getStudentDiscount } from '@http/endpoints'
import FormattedMessage from '@i18n/components/FormattedMessage'
import logger from '@logger'
import { ROUTES } from '@router'
import { trackClick } from '@tracking/events'

import Steps from '../components/Steps.vue'

import translations from './StudentDiscount.translations'

const MARKETING_PARAMS = ['utm_source', 'utm_campaign']

export default {
  components: {
    RevButton,
    RevLink,
    RevContainer,
    FormattedMessage,
    RevIllustration,
    Steps,
  },

  async asyncData({ store, error }) {
    try {
      const {
        payload: { sheerIdUrl, sheerIdCallbackLink },
      } = await store.dispatch('http/request', {
        request: getStudentDiscount,
      })

      return {
        sheerIdUrl,
        sheerIdCallbackLink,
      }
    } catch (err) {
      const message = `[LIF][Student Offer] ${err.message}`
      logger.error(message, err)

      return error({ statusCode: err.status_code, message: err.message })
    }
  },

  data() {
    return {
      sheerIdUrl: null,
      sheerIdCallbackLink: null,
    }
  },

  head() {
    return {
      title: this.$t(translations.title),
      meta: [
        {
          hid: 'description',
          name: 'description',
          content: this.$t(translations.subtitle),
        },
        {
          content: this.$t(translations.bigTitle),
          property: 'og:title',
          hid: 'og:title',
        },
        {
          content: this.$t(translations.subtitle),
          property: 'og:description',
          hid: 'og:description',
        },
        {
          content: this.$t(translations.bigTitle),
          property: 'twitter:title',
          hid: 'twitter:title',
        },
        {
          content: this.$t(translations.subtitle),
          property: 'twitter:description',
          hid: 'twitter:description',
        },
      ],
    }
  },

  computed: {
    ...mapGetters({
      baseURL: 'config/baseURL',
      country: 'config/country',
      lang: 'config/lang',
      encryptedUserId: 'auth/encryptedId',
      isAuthenticated: 'auth/status',
      isNewAuthEnabled: 'auth/isNewAuthEnabled',
    }),
    translations: () => translations,
    redirectURLParams() {
      const redirectParams = omit(this.$route.query, MARKETING_PARAMS)

      if (isEmpty(redirectParams)) {
        return ''
      }

      return `?${redirectParams}`
    },
    externalFormUrl() {
      const studentDealsLandingLink = this.$navigation.resolve(
        this.sheerIdCallbackLink,
      ).href

      // SheerID expects a lang as a locale... except for UK and Ireland where it expects a locale with this format: 'en-GB'
      const isApiExpectingEnglishLocale =
        this.country === COUNTRIES.GB || this.country === COUNTRIES.IE

      const params = {
        locale: isApiExpectingEnglishLocale ? 'en-GB' : this.lang,
        country: this.country,
        userid: this.encryptedUserId || 'USER_ID',
        redirectURL: `${this.baseURL}${studentDealsLandingLink}${this.redirectURLParams}`,
      }

      return `${this.sheerIdUrl}/?${stringify(params)}`
    },
    redirectRoute() {
      if (this.isAuthenticated) {
        return this.externalFormUrl
      }

      let authLink = {
        name: ROUTES.AUTH.REGISTER,
        query: { next: this.externalFormUrl, type: 'external' },
      }
      if (this.isNewAuthEnabled) {
        authLink = {
          name: ROUTES.AUTH_OAUTH2.LOGIN,
          query: { next: this.externalFormUrl, type: 'external' },
        }
      }

      return authLink
    },
    ROUTES: () => ROUTES,
  },

  methods: {
    trackEvent(name) {
      trackClick({ name, zone: 'student_discount' })
    },
  },
}
